// Analytics Tracking Utility for CX Sage
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class AnalyticsTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionStartTime = Date.now();
    this.currentPageStartTime = Date.now();
    
    // Detect device and browser info
    this.deviceInfo = this.getDeviceInfo();
    
    // Track page unload for session duration
    window.addEventListener('beforeunload', () => this.endSession());
    
    // Track clicks
    document.addEventListener('click', (e) => this.trackClick(e), true);
  }
  
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }
  
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('analytics_visitor_id');
    if (!visitorId) {
      visitorId = this.generateId();
      localStorage.setItem('analytics_visitor_id', visitorId);
    }
    return visitorId;
  }
  
  generateId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  getDeviceInfo() {
    const ua = navigator.userAgent;
    
    // Detect device type
    let deviceType = 'desktop';
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      deviceType = 'tablet';
    } else if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      deviceType = 'mobile';
    }
    
    // Detect browser
    let browser = 'Unknown';
    if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) browser = 'Opera';
    else if (ua.indexOf('Trident') > -1) browser = 'IE';
    else if (ua.indexOf('Edge') > -1) browser = 'Edge';
    else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (ua.indexOf('Safari') > -1) browser = 'Safari';
    
    // Detect OS
    let os = 'Unknown';
    if (ua.indexOf('Win') > -1) os = 'Windows';
    else if (ua.indexOf('Mac') > -1) os = 'MacOS';
    else if (ua.indexOf('Linux') > -1) os = 'Linux';
    else if (ua.indexOf('Android') > -1) os = 'Android';
    else if (ua.indexOf('iOS') > -1) os = 'iOS';
    
    return {
      deviceType,
      browser,
      os,
      userAgent: ua,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    };
  }
  
  async trackPageView() {
    let pageLoadTime = null;
    try {
      const navEntry = window.performance && window.performance.getEntriesByType
        ? window.performance.getEntriesByType('navigation')[0]
        : null;
      if (navEntry && navEntry.loadEventEnd > 0) {
        pageLoadTime = Math.round(navEntry.loadEventEnd - navEntry.startTime);
      } else if (window.performance && window.performance.timing) {
        const t = window.performance.timing;
        if (t.loadEventEnd > 0 && t.navigationStart > 0) {
          pageLoadTime = t.loadEventEnd - t.navigationStart;
        }
      }
      // Only report a sane, positive value (<= 10 min); otherwise send null
      if (pageLoadTime !== null && (pageLoadTime <= 0 || pageLoadTime > 600000)) {
        pageLoadTime = null;
      }
    } catch (e) {
      pageLoadTime = null;
    }
    
    const data = {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      page_url: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || null,
      user_agent: this.deviceInfo.userAgent,
      device_type: this.deviceInfo.deviceType,
      browser: this.deviceInfo.browser,
      os: this.deviceInfo.os,
      screen_resolution: this.deviceInfo.screenResolution,
      page_load_time: pageLoadTime
    };
    
    try {
      await fetch(`${API}/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
    
    this.currentPageStartTime = Date.now();
  }
  
  async trackClick(event) {
    const element = event.target;
    const elementType = element.tagName.toLowerCase();
    
    // Only track meaningful clicks (buttons, links, etc.)
    if (!['a', 'button', 'input'].includes(elementType)) {
      return;
    }
    
    const data = {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      page_url: window.location.pathname,
      element_type: elementType,
      element_text: element.innerText || element.value || null,
      element_id: element.id || null
    };
    
    try {
      await fetch(`${API}/analytics/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.error('Click tracking error:', error);
    }
  }
  
  async endSession() {
    const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    
    const data = {
      session_id: this.sessionId,
      duration: duration
    };
    
    try {
      // Use sendBeacon for reliable tracking on page unload
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      navigator.sendBeacon(`${API}/analytics/session-end`, blob);
    } catch (error) {
      console.error('Session end tracking error:', error);
    }
  }
}

// Initialize analytics tracker
let analyticsTracker = null;

export const initAnalytics = () => {
  if (!analyticsTracker) {
    analyticsTracker = new AnalyticsTracker();
  }
  return analyticsTracker;
};

export const trackPageView = () => {
  if (!analyticsTracker) {
    analyticsTracker = new AnalyticsTracker();
  }
  analyticsTracker.trackPageView();
};

export default AnalyticsTracker;
